import React, { useState, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { WalletIcon, AlertTriangleIcon, ArrowLeft } from 'lucide-react';
import VaultAdmin from './components/VaultAdmin';
import DexManager from './components/DexManager';
import { formatAddress } from './utils/helpers';
import web3Provider from './utils/web3Provider';
import { TokenLogos } from './assets/tokenLogos';
import './index.css';

function AdminPage() {
  const [account, setAccount] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [networkError, setNetworkError] = useState(null);
  const [activeTab, setActiveTab] = useState('vault'); // 'vault' or 'dex'

  useEffect(() => {
    // Check if already connected
    const checkConnection = async () => {
      try {
        if (window.ethereum && window.ethereum.selectedAddress) {
          const connectionResult = await web3Provider.connect();
          setAccount(connectionResult.account);
          
          if (connectionResult.chainId !== 250) {
            setNetworkError('Please switch to Fantom network');
          } else {
            setNetworkError(null); // Clear network error if on correct network
          }
        }
      } catch (error) {
        console.error('Connection check failed:', error);
      }
    };

    checkConnection();

    // Listen for account changes
    if (window.ethereum) {
      window.ethereum.on('accountsChanged', (accounts) => {
        if (accounts.length === 0) {
          setAccount(null);
          setConnectionError(null);
          setNetworkError(null);
        } else {
          setAccount(accounts[0]);
        }
      });

      window.ethereum.on('chainChanged', (chainId) => {
        const numericChainId = parseInt(chainId, 16);
        if (numericChainId !== 250) {
          setNetworkError('Please switch to Fantom network');
        } else {
          setNetworkError(null);
        }
        window.location.reload();
      });
    }

    return () => {
      if (window.ethereum) {
        window.ethereum.removeAllListeners('accountsChanged');
        window.ethereum.removeAllListeners('chainChanged');
      }
    };
  }, []);

  const handleConnect = async () => {
    setIsConnecting(true);
    setConnectionError(null);
    setNetworkError(null);

    try {
      const result = await web3Provider.connect();
      setAccount(result.account);
      
      if (result.chainId !== 250) {
        setNetworkError('Please switch to Fantom network');
      } else {
        setNetworkError(null); // Clear network error if on correct network
      }
    } catch (error) {
      console.error('Connection failed:', error);
      setConnectionError(error.message);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = () => {
    setAccount(null);
    setConnectionError(null);
    setNetworkError(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-950 to-black relative overflow-hidden">
      {/* Background Animation */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-72 h-72 bg-blue-600 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-float"></div>
        <div className="absolute top-0 right-0 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-float animation-delay-2000"></div>
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-blue-700 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-float animation-delay-4000"></div>
      </div>

      {/* Header */}
      <header className="relative z-10 p-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => window.location.href = '/'}
              className="flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors duration-200 border border-white/20"
            >
              <ArrowLeft className="w-4 h-4 text-white" />
              <span className="text-white font-medium">Back to Swap</span>
            </button>
            <div className="flex items-center space-x-3">
              <img src={TokenLogos.GIGACHAD} alt="Chad" className="w-14 h-14 rounded-lg" />
              <div>
                <h1 className="text-2xl font-bold text-white font-orbitron">Chad Admin</h1>
                <p className="text-white/60 text-sm">Vault Management</p>
              </div>
            </div>
          </div>
          
          {/* Wallet Connection */}
          <div className="flex items-center space-x-4">
            {networkError && (
              <div className="flex items-center space-x-2 px-3 py-2 bg-red-500/20 border border-red-400/50 text-red-300 rounded-lg">
                <AlertTriangleIcon className="w-4 h-4" />
                <span className="text-sm font-medium">{networkError}</span>
              </div>
            )}

            {account ? (
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2 px-4 py-2 bg-green-500/20 border border-green-400/50 text-green-300 rounded-lg">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span className="font-medium">{formatAddress(account)}</span>
                </div>
                <button
                  onClick={handleDisconnect}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-lg font-medium transition-colors duration-200"
                >
                  Disconnect
                </button>
              </div>
            ) : (
              <button
                onClick={handleConnect}
                disabled={isConnecting}
                className="flex items-center space-x-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white rounded-xl font-semibold transition-colors duration-200 disabled:cursor-not-allowed"
              >
                <WalletIcon className="w-5 h-5" />
                <span>{isConnecting ? 'Connecting...' : 'Connect Wallet'}</span>
              </button>
            )}
          </div>
        </div>

        {connectionError && (
          <div className="max-w-7xl mx-auto mt-4">
            <div className="flex items-center space-x-2 px-4 py-3 bg-red-500/20 border border-red-400/50 text-red-300 rounded-lg">
              <AlertTriangleIcon className="w-5 h-5" />
              <span>{connectionError}</span>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="relative z-10 flex-1 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Tab Navigation */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-1">
              <button
                onClick={() => setActiveTab('vault')}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                  activeTab === 'vault'
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
              >
                Vault Management
              </button>
              <button
                onClick={() => setActiveTab('dex')}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                  activeTab === 'dex'
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
              >
                DEX Management (V2)
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex justify-center pt-4">
            {activeTab === 'vault' ? (
              <VaultAdmin 
                account={account} 
                signer={web3Provider.getSigner()} 
              />
            ) : (
              <div className="w-full max-w-4xl">
                <DexManager />
              </div>
            )}
          </div>

          {!account && (
            <div className="text-center mt-12">
              <p className="text-xl text-white/70 max-w-2xl mx-auto font-rajdhani">
                {activeTab === 'vault' 
                  ? 'Connect your wallet to access the vault admin interface'
                  : 'DEX management is available for configuring V2 liquidity sources'
                }
              </p>
            </div>
          )}

          {/* Community Message */}
          <div className="mt-16 text-center">
            <p className="text-lg text-white/80 max-w-3xl mx-auto font-rajdhani">
              Management tools for the LIF3 community 💙
            </p>
          </div>
        </div>
      </main>

      {/* Toast Notifications */}
      <Toaster
        position="bottom-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
          },
          success: {
            iconTheme: {
              primary: '#10B981',
              secondary: 'white',
            },
          },
          error: {
            iconTheme: {
              primary: '#EF4444',
              secondary: 'white',
            },
          },
        }}
      />
    </div>
  );
}

export default AdminPage;